import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  Project,
  CreateProjectDTO,
  UpdateProjectDTO,
  ProjectFilters,
  ImageUploadResponse,
  ProjectStatus,
  ProjectIconOption,
  EquivalenceOption,
  PartnerCompanyConfig,
  CreatePartnerCompanyDTO,
  UpdatePartnerCompanyDTO,
  CompanySearchResult,
  OdooProduct,
} from '../models/project.model';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private http = inject(HttpClient);

  // Signal for reactive state management
  public projectsSignal: WritableSignal<Project[]> = signal([]);
  public selectedProjectSignal: WritableSignal<Project | null> = signal(null);

  /**
   * Get all projects with optional filters (admin view bypasses company restrictions)
   */
  getAllProjects(filters?: ProjectFilters): Observable<{ data: Project[] }> {
    let params = new HttpParams();

    // Admin view: bypass company restrictions to see all projects
    params = params.set('adminView', 'true');

    if (filters) {
      if (filters.q) params = params.set('q', filters.q);
      if (filters.status) params = params.set('status', filters.status);
      if (filters.projectType) params = params.set('projectType', filters.projectType);
      if (filters.region) params = params.set('region', filters.region);
      if (filters.certificationStandard) params = params.set('certificationStandard', filters.certificationStandard);
      if (filters.isCertifiedOffset !== undefined) params = params.set('isCertifiedOffset', filters.isCertifiedOffset.toString());
      if (filters.page) params = params.set('page', filters.page.toString());
      if (filters.limit) params = params.set('limit', filters.limit.toString());
    }

    return this.http.get<{ data: Project[] }>(`${environment.apiUrl}/projects`, { params });
  }

  /**
   * Get project by ID (admin endpoint with full details)
   */
  getProjectById(id: string): Observable<{ data: Project }> {
    return this.http.get<{ data: Project }>(`${environment.apiUrl}/admin/projects/${id}`);
  }

  /**
   * Create a new project (admin only)
   */
  createProject(data: CreateProjectDTO): Observable<{ data: Project }> {
    return this.http.post<{ data: Project }>(`${environment.apiUrl}/admin/projects`, data);
  }

  /**
   * Update an existing project (admin only)
   */
  updateProject(id: string, data: UpdateProjectDTO): Observable<{ data: Project }> {
    return this.http.put<{ data: Project }>(`${environment.apiUrl}/admin/projects/${id}`, data);
  }

  /**
   * Delete a project (admin only)
   */
  deleteProject(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/admin/projects/${id}`);
  }

  /**
   * Change project status (admin only)
   */
  changeStatus(id: string, status: ProjectStatus): Observable<{ data: Project }> {
    return this.http.put<{ data: Project }>(`${environment.apiUrl}/admin/projects/${id}/status`, { status });
  }

  /**
   * Upload project image (admin only)
   * @param id Project ID
   * @param file Image file (jpg, jpeg, png)
   * @param type Image type (main, certificate, social)
   */
  uploadImage(id: string, file: File, type: 'main' | 'certificate' | 'social'): Observable<{ data: ImageUploadResponse }> {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);

    return this.http.post<{ data: ImageUploadResponse }>(
      `${environment.apiUrl}/admin/projects/${id}/images`,
      formData
    );
  }

  /**
   * Helper: Load all projects and update signal
   */
  loadProjects(filters?: ProjectFilters): void {
    this.getAllProjects(filters).subscribe({
      next: (response) => {
        this.projectsSignal.set(response.data);
      },
      error: (err) => {
        console.error('[ProjectService] Failed to load projects:', err);
      },
    });
  }

  /**
   * Helper: Load single project and update signal
   */
  loadProject(id: string): void {
    this.getProjectById(id).subscribe({
      next: (response) => {
        this.selectedProjectSignal.set(response.data);
      },
      error: (err) => {
        console.error('[ProjectService] Failed to load project:', err);
      },
    });
  }

  /**
   * Helper: Clear selected project
   */
  clearSelectedProject(): void {
    this.selectedProjectSignal.set(null);
  }

  /**
   * Get available project icons for certificate configuration
   */
  getProjectIcons(): Observable<{ data: ProjectIconOption[] }> {
    return this.http.get<{ data: ProjectIconOption[] }>(
      `${environment.apiUrl}/admin/certificate-options/icons`
    );
  }

  /**
   * Get available CO2 equivalences for certificate configuration
   */
  getEquivalences(): Observable<{ data: EquivalenceOption[] }> {
    return this.http.get<{ data: EquivalenceOption[] }>(
      `${environment.apiUrl}/admin/certificate-options/equivalences`
    );
  }

  // ============================================
  // Partner Company Management (Project Restrictions)
  // ============================================

  /**
   * Get companies configured for a project
   */
  getPartnerCompanies(projectId: string): Observable<{ data: PartnerCompanyConfig[] }> {
    return this.http.get<{ data: PartnerCompanyConfig[] }>(
      `${environment.apiUrl}/admin/projects/${projectId}/partner-companies`
    );
  }

  /**
   * Add a company configuration to a project
   */
  addPartnerCompany(
    projectId: string,
    data: CreatePartnerCompanyDTO
  ): Observable<{ data: PartnerCompanyConfig }> {
    return this.http.post<{ data: PartnerCompanyConfig }>(
      `${environment.apiUrl}/admin/projects/${projectId}/partner-companies`,
      data
    );
  }

  /**
   * Update a partner company configuration
   */
  updatePartnerCompany(
    projectId: string,
    companyId: string,
    data: UpdatePartnerCompanyDTO
  ): Observable<{ data: PartnerCompanyConfig }> {
    return this.http.put<{ data: PartnerCompanyConfig }>(
      `${environment.apiUrl}/admin/projects/${projectId}/partner-companies/${companyId}`,
      data
    );
  }

  /**
   * Remove a partner company from a project
   */
  removePartnerCompany(projectId: string, companyId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiUrl}/admin/projects/${projectId}/partner-companies/${companyId}`
    );
  }

  /**
   * Search companies for autocomplete
   */
  searchCompanies(query: string): Observable<{ data: CompanySearchResult[] }> {
    const params = new HttpParams().set('q', query);
    return this.http.get<{ data: CompanySearchResult[] }>(
      `${environment.apiUrl}/admin/companies/search`,
      { params }
    );
  }

  // ============================================
  // Odoo Integration
  // ============================================

  /**
   * Get available Odoo products for project configuration
   */
  getOdooProducts(): Observable<{ success: boolean; products: OdooProduct[] }> {
    return this.http.get<{ success: boolean; products: OdooProduct[] }>(
      `${environment.apiUrl}/admin/odoo/products`
    );
  }
}
